// Diagnostic logger — surface iOS WebView errors that users can read.
//
// Why: when WYNG crashes inside the Capacitor WebView on a user's iPhone,
// the developer cannot attach Safari Web Inspector unless the device is
// USB-connected to a Mac. End users have neither. This module captures
// ALL errors (window.onerror, unhandled promise rejections, React error
// boundary catches, and explicit log calls) and persists them to
// localStorage + posts to /api/client-errors. The Welcome page surfaces
// the latest entries via a "Diagnostiek" link so a frustrated user can
// share a screenshot of the actual stack trace.

const STORAGE_KEY = "wyng_diag_log";
const MAX_ENTRIES = 50;

function readLog() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch { return []; }
}

function writeLog(entries) {
    try {
        localStorage.setItem(STORAGE_KEY,
            JSON.stringify(entries.slice(-MAX_ENTRIES)));
    } catch { /* quota full — silently drop oldest */ }
}

export function diagLog(level, source, message, extra) {
    const entry = {
        ts:     new Date().toISOString(),
        level,                          // "error" | "warn" | "info"
        source,                         // "boundary" | "window" | "rejection" | ...
        message: String(message ?? "")
            .slice(0, 500),
        extra: extra ? String(extra).slice(0, 1000) : null,
        ua:    typeof navigator !== "undefined"
            ? (navigator.userAgent || "").slice(0, 200) : null,
        path:  typeof window !== "undefined" ? window.location.pathname : null,
    };
    try {
        const log = readLog();
        log.push(entry);
        writeLog(log);
    } catch { /* localStorage might be locked */ }

    // Fire-and-forget upload to the backend so we can see it server-side
    // (helps in production where we can grep MongoDB later). Uses sendBeacon
    // for resilience — survives page navigation. Plain text/plain to avoid
    // CORS preflight on the cold-launch path.
    try {
        const blob = new Blob([JSON.stringify(entry)],
                              { type: "text/plain" });
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/client-errors`;
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            navigator.sendBeacon(url, blob);
        } else if (typeof fetch !== "undefined") {
            fetch(url, { method: "POST", headers: { "Content-Type": "text/plain" },
                         body: JSON.stringify(entry), keepalive: true })
                .catch(() => {});
        }
    } catch { /* network failed — entry already persisted locally */ }
}

export function readDiagLog() { return readLog(); }
export function clearDiagLog() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

// One-time global error hooks. Mounted exactly once from App.js.
let installed = false;
export function installGlobalErrorHandlers() {
    if (installed) return;
    installed = true;

    if (typeof window === "undefined") return;

    window.addEventListener("error", (e) => {
        diagLog("error", "window",
            e?.message || "window.error fired",
            e?.error?.stack || `${e?.filename}:${e?.lineno}:${e?.colno}`);
    });

    window.addEventListener("unhandledrejection", (e) => {
        const reason = e?.reason;
        const msg = reason?.message || String(reason || "unhandled rejection");
        diagLog("error", "rejection", msg, reason?.stack || null);
    });

    // Breadcrumb so we know JS executed at all in the cold-launched WebView.
    diagLog("info", "boot",
        `JS booted on ${window.location.protocol}//${window.location.host}`);
}
